import { getDB, readProduct, cacheProducts, readProducts } from "./db";
import api from "./api";

/** Queue helpers ---------------------------------------------------------- */
export async function enqueue(item) {
  const db = await getDB();
  const created_at = new Date().toISOString();
  return db.add("queue", { ...item, created_at, attempts: 0, last_error: null });
}

export async function getQueue() {
  const db = await getDB();
  return db.getAll("queue");
}

export async function queueSize() {
  const db = await getDB();
  return db.count("queue");
}

export async function removeFromQueue(id) {
  const db = await getDB();
  return db.delete("queue", id);
}

async function bumpQueueItem(id, patch) {
  const db = await getDB();
  const tx = db.transaction("queue", "readwrite");
  const item = await tx.store.get(id);
  if (item) await tx.store.put({ ...item, ...patch });
  await tx.done;
}

/** uuid4 (lightweight, no dep) -------------------------------------------- */
export function uuid4() {
  if (crypto?.randomUUID) return crypto.randomUUID();
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/** Optimistic local mutations -------------------------------------------- */
async function localAdjustStock(productId, variantId, delta) {
  const p = await readProduct(productId);
  if (!p) return null;
  const next = {
    ...p,
    variants: (p.variants || []).map((v) =>
      v.variant_id === variantId ? { ...v, stock: Math.max(0, Number(v.stock || 0) + Number(delta)) } : v
    ),
  };
  // upsert just this product back into the cache
  const products = (await readProducts()).map((pp) => (pp.product_id === productId ? next : pp));
  await cacheProducts(products);
  return next;
}

/** Public API: enqueue actions ------------------------------------------- */
export async function enqueueSale({ event_id = null, product_id, variant_id, quantity = 1, unit_price }) {
  const idempotency_key = uuid4();
  const client_created_at = new Date().toISOString();
  const updated = await localAdjustStock(product_id, variant_id, -Number(quantity));
  await enqueue({
    kind: "sale",
    payload: { event_id, product_id, variant_id, quantity, unit_price, idempotency_key, client_created_at },
  });
  return { idempotency_key, product: updated };
}

export async function enqueueAdjust({ product_id, variant_id, delta }) {
  const idempotency_key = uuid4();
  const updated = await localAdjustStock(product_id, variant_id, delta);
  await enqueue({
    kind: "adjust",
    payload: { product_id, variant_id, delta: Number(delta), idempotency_key },
  });
  return { idempotency_key, product: updated };
}

/** Sync engine ----------------------------------------------------------- */
let syncing = false;

export async function processQueue() {
  if (syncing) return { ran: false, reason: "in-progress" };
  syncing = true;
  let processed = 0;
  let failed = 0;
  try {
    const items = await getQueue();
    for (const it of items) {
      try {
        if (it.kind === "sale") {
          await api.post("/sales", it.payload);
        } else if (it.kind === "adjust") {
          await api.post(
            `/products/${it.payload.product_id}/variants/${it.payload.variant_id}/adjust`,
            null,
            { params: { delta: it.payload.delta, idempotency_key: it.payload.idempotency_key } }
          );
        } else {
          // unknown kind — drop it
        }
        await removeFromQueue(it.id);
        processed += 1;
      } catch (e) {
        failed += 1;
        const attempts = (it.attempts || 0) + 1;
        const last_error = String(e?.response?.data?.detail || e?.message || "unknown");
        // Dead-letter after 20 attempts to prevent forever-retry of bad payloads
        if (attempts >= 20) {
          await removeFromQueue(it.id);
        } else {
          await bumpQueueItem(it.id, { attempts, last_error });
        }
      }
    }
  } finally {
    syncing = false;
  }
  return { ran: true, processed, failed };
}
